<template>
  <b-container v-if="order">
    <b-row no-gutters class="mb-3 overflow-auto">
      <table class="first-left-table">
        <thead>
          <tr>
            <th class="d-flex">
              Kitchen Totals
              <span id="kitchen-orders-kitchen-totals" class="icon ml-2">
                <img
                  id="hours-tooltip"
                  :src="asset('/imgs/icons/info.svg')"
                  alt="hours info"
                  class="mb-1"
                />
              </span>
            </th>
            <b-tooltip target="kitchen-orders-kitchen-totals" triggers="hover">
              Hover over existing modifier option rows to edit their name,
              price, and pre-selected status.
            </b-tooltip>
            <th width="30%" class="pr-sm-1">
              {{
                order.fulfillment_center ? order.fulfillment_center.name : null
              }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Subtotal</td>
            <td>
              {{ (order.subtotal / 100) | currency }}
            </td>
          </tr>
          <tr>
            <td>Discount</td>
            <td>-{{ (order.discount_total / 100) | currency }}</td>
          </tr>
          <tr>
            <td>Reimbursable Discounts</td>
            <td>
              {{ (order.reimbursable_discount_total / 100) | currency }}
            </td>
          </tr>
          <tr>
            <td>Tax</td>
            <td>
              {{ (order.tax_total / 100) | currency }}
            </td>
          </tr>
          <tr>
            <td>Discount Tax</td>
            <td>
              {{ (order.discount_tax_total / 100) | currency }}
            </td>
          </tr>
          <tr>
            <td>Delivery Fee</td>
            <td>-{{ (order.delivery_fee / 100) | currency }}</td>
          </tr>
          <tr>
            <td>Tip</td>
            <td>
              {{ (order.tip_total / 100) | currency }}
            </td>
          </tr>
          <tr>
            <td>Zuul Fee</td>
            <td>-{{ (order.zuul_commission / 100) | currency }}</td>
          </tr>
          <tr>
            <td>Facility Fee</td>
            <td>-{{ (order.facility_commission / 100) | currency }}</td>
          </tr>
          <tr>
            <td>Partner Fee</td>
            <td>-{{ (order.partner_commission / 100) | currency }}</td>
          </tr>
          <tr>
            <td>Employer Fee</td>
            <td>-{{ (order.employer_commission / 100) | currency }}</td>
          </tr>
          <tr class="divider-row">
            <td>CC Processing Fee</td>
            <td>-{{ (order.processing_fee / 100) | currency }}</td>
          </tr>
          <tr class="divider-row">
            <td>Total Kitchen Fees</td>
            <td>-{{ (order.fee_total / 100) | currency }}</td>
          </tr>
          <tr>
            <td>
              <strong>Net Total</strong
              ><span
                id="kitchen-order-fulfillment_center-name"
                class="icon ml-3"
              >
                <img
                  id="hours-tooltip"
                  class="mb-1"
                  :src="asset('/imgs/icons/info.svg')"
                  alt="hours info"
                />
              </span>
            </td>
            <b-tooltip
              target="kitchen-order-fulfillment_center-name"
              triggers="hover"
            >
              This is the original total paid to the kitchen. If this order was
              refunded, see Refunds table below to see the final post-refund
              payment.
            </b-tooltip>
            <td>
              <strong>{{ (order.net_total / 100) | currency }}</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </b-row>
    <b-row
      v-if="order.fulfillment_center_order_refunds.length"
      no-gutters
      class="mb-3 overflow-auto"
    >
      <table>
        <thead>
          <tr>
            <th>Refund Totals</th>
            <th>Initial Total</th>
            <th
              v-for="(refund, index) in order.fulfillment_center_order_refunds"
              :key="refund.id"
            >
              Refund #{{ index + 1 }}
            </th>
            <th>Total</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              {{
                order.fulfillment_center ? order.fulfillment_center.name : null
              }}
            </td>
            <td>
              {{ (order.net_total / 100) | currency }}
            </td>
            <td
              v-for="refund in order.fulfillment_center_order_refunds"
              :key="refund.id"
            >
              {{ (refund.net_total / 100) | currency }}
            </td>
            <td>{{ (netRefundTotal / 100) | currency }}</td>
            <td>
              <template v-if="order.fulfillment_center_order_refunds.length">
                {{
                  order.fulfillment_center_order_refunds[0].network_order_refund
                    .notes
                }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </b-row>
    <b-row
      v-if="order.fulfillment_center_order_coupons.length"
      no-gutters
      class="mb-3 overflow-auto"
    >
      <table>
        <thead>
          <tr>
            <th>Coupon Code</th>
            <th>Type</th>
            <th>Amount</th>
            <th>Target</th>
            <th>Discount</th>
            <th>Discount Tax</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="kitchenCoupon in order.fulfillment_center_order_coupons"
            :key="kitchenCoupon.id"
          >
            <td>
              {{ kitchenCoupon.order_coupon.code }}
            </td>
            <td>{{ kitchenCoupon.order_coupon.type | capitalize }}</td>
            <td>
              <template v-if="kitchenCoupon.order_coupon.type === 'percent'">
                {{ kitchenCoupon.order_coupon.amount }}% off
              </template>
              <template v-else>
                {{ (kitchenCoupon.order_coupon.amount / 100) | currency }} off
              </template>
            </td>
            <td>{{ kitchenCoupon.order_coupon.target | capitalize }}</td>
            <td>{{ (kitchenCoupon.discount / 100) | currency }}</td>
            <td>{{ (kitchenCoupon.discount_tax / 100) | currency }}</td>
          </tr>
        </tbody>
      </table>
    </b-row>
  </b-container>
</template>

<script>
export default {
  props: {
    order: {
      type: Object,
      default() {
        return { id: null };
      },
    },
  },
  computed: {
    netRefundTotal() {
      let totalRefunds = 0;

      this.order.fulfillment_center_order_refunds.forEach((refund) => {
        totalRefunds += refund.net_total;
      });

      return this.order.net_total - totalRefunds;
    },
  },
};
</script>
